import React from "react"
import { ButtonWrapper } from "./ButtonWrapper"
import RowContainer from "./Layout/RowContainer"
import { Typography, typographyTypes } from "./Typography"
import ColumnContainer from "./Layout/ColumnContainer"
import Thumbnail from "./Thumbnail"
import Icon from "./Icon"

export interface CarouselChild {
  id: string
  imageUrl: string
  imageAltText: string
  imageLabel: string
  imageDescription?: string
  imageAriaLabel: string
  isLoading?: boolean
}

export interface ImageCarouselProps {
  /** An array of carousel child objects. */
  carouselChildren: CarouselChild[]
  /** An object containing the text for the navigation buttons. */
  text: {
    /** The text for the next button. */
    nextButton: string
    /** The text for the previous button. */
    previousButton: string
  }
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  carouselChildren,
  text,
}) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0)

  const onPreviousClick = () => {
    const newIndex = selectedIndex === 0 ? selectedIndex : selectedIndex - 1
    setSelectedIndex(newIndex)
  }
  const onNextClick = () => {
    const newIndex =
      selectedIndex === carouselChildren.length - 1
        ? selectedIndex
        : selectedIndex + 1
    setSelectedIndex(newIndex)
  }
  const onChildClick = (index: number) => {
    setSelectedIndex(index)
  }

  const getButtonStyle = (index: number) => {
    const commonStyles =
      "border-2 border-transparent hover:bg-[#F4FBFD] hover:border-gray-200"
    const selectedStyles = "!border-b-primary-blue"
    const combinedStyle = `${commonStyles} ${
      index === selectedIndex ? selectedStyles : ""
    }`

    return combinedStyle
  }

  return (
    <div className="grid grid-cols-[auto_640px_auto] gap-5">
      <ButtonWrapper
        onClick={onPreviousClick}
        styleOverrides="items-center justify-self-end"
        disabled={selectedIndex === 0}
      >
        <RowContainer styleOverrides="space-x-0">
          <Icon iconType="chevronLeft" />
          <Typography
            text={text.previousButton}
            variant={typographyTypes.NORMAL}
          />
        </RowContainer>
      </ButtonWrapper>
      <ColumnContainer styleOverrides="mt-4 space-y-4 w-[640px]">
        <RowContainer styleOverrides="flex-wrap gap-x-4 space-x-0">
          {carouselChildren.map((child, index) => (
            <ButtonWrapper
              key={child.id}
              onClick={() => onChildClick(index)}
              styleOverrides={getButtonStyle(index)}
              tabIndex={-1}
            >
              <Typography
                text={child.imageLabel}
                variant={typographyTypes.PANEL_ITEM}
                styleOverrides="text-base"
              />
            </ButtonWrapper>
          ))}
        </RowContainer>
        <Thumbnail
          id={carouselChildren[selectedIndex].id}
          imageUrl={carouselChildren[selectedIndex].imageUrl}
          imageAltText={carouselChildren[selectedIndex].imageAltText}
          ariaLabel={carouselChildren[selectedIndex].imageAriaLabel}
          styleOverrides="w-[640px] h-[480px]"
          isLoading={carouselChildren[selectedIndex].isLoading}
        />
        <Typography
          text={carouselChildren[selectedIndex].imageDescription}
          variant={typographyTypes.NORMAL}
        />
      </ColumnContainer>
      <ButtonWrapper
        onClick={onNextClick}
        styleOverrides="items-center"
        disabled={selectedIndex === carouselChildren.length - 1}
      >
        <RowContainer styleOverrides="w-fit space-x-0">
          <Typography text={text.nextButton} variant={typographyTypes.NORMAL} />
          <Icon iconType="chevronRight" />
        </RowContainer>
      </ButtonWrapper>
    </div>
  )
}

export default ImageCarousel
